import { h } from 'preact';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';

import AskUser from './AskUser.jsx';
import { submitCopilotFeedback } from './chat.js';

/**
 * Thumbs up/down on Copilot replies, Claude-Code style (port of
 * reactdashboard's CopilotRail/replyFeedback.tsx): the rating is filed the
 * moment it is clicked (telemetry), and a thumbs-down opens an inline
 * ask_user-style form — "What can we do better?" — whose answer is filed as
 * a second, text-bearing event that drives the platform feedback intake.
 */

export const FEEDBACK_QUESTION = 'What can we do better?';
const EXCERPT_LIMIT = 2000;

function excerpt(text) {
  return typeof text === 'string' ? text.slice(0, EXCERPT_LIMIT) : undefined;
}

/** Thumbs up/down state + transport, port of reactdashboard's useReplyFeedback. */
export function useReplyFeedback(messages) {
  const [feedback, setFeedback] = useState({});
  const filedRef = useRef({});
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const contextFor = index => {
    const current = messagesRef.current;
    const prompt = current
      .slice(0, index)
      .reverse()
      .find(message => message.role === 'user');
    return {
      userPrompt: excerpt(prompt && prompt.text),
      assistantText: excerpt(current[index] && current[index].text),
    };
  };

  const rate = (index, rating) => {
    if (filedRef.current[index] === rating) return;
    filedRef.current[index] = rating;
    setFeedback(current => ({
      ...current,
      [index]: { rating, ask: rating === 'down' ? 'open' : undefined },
    }));
    const runId = messagesRef.current[index] && messagesRef.current[index].runId;
    if (!runId) return;
    submitCopilotFeedback({ runId, rating, context: contextFor(index) }).catch(error =>
      console.warn('[copilot] rating not recorded', error)
    );
  };

  const sendText = async (index, text) => {
    const clean = (text || '').trim();
    const runId = messagesRef.current[index] && messagesRef.current[index].runId;
    if (clean && runId) {
      await submitCopilotFeedback({ runId, rating: 'down', text: clean, context: contextFor(index) });
    }
    setFeedback(current => ({
      ...current,
      [index]: { rating: (current[index] && current[index].rating) || 'down', ask: 'sent' },
    }));
  };

  const dismiss = index =>
    setFeedback(current => ({
      ...current,
      [index]: { rating: (current[index] && current[index].rating) || 'down', ask: undefined },
    }));

  const reset = useCallback(() => {
    filedRef.current = {};
    setFeedback({});
  }, []);

  return {
    ratingOf: index => feedback[index] && feedback[index].rating,
    showsFollowUp: index => Boolean(feedback[index] && feedback[index].ask !== undefined),
    rate,
    sendText,
    dismiss,
    reset,
  };
}

export function ReplyFeedbackForm(props) {
  const { index, feedback } = props;
  const feedbackRef = useRef(feedback);
  feedbackRef.current = feedback;
  const request = useMemo(
    () => ({
      id: `feedback-${index}`,
      questions: [
        {
          qtype: 'text',
          content: FEEDBACK_QUESTION,
          multiline: true,
          required: true,
          placeholder: 'What went wrong, and what did you expect instead?',
        },
      ],
      submit: answers => feedbackRef.current.sendText(index, answers['0'] || ''),
    }),
    [index]
  );
  return (
    <AskUser
      request={request}
      title="Sorry about that."
      submitLabel="Send feedback"
      submittedLabel="✓ Thanks — feedback sent to the team"
      onDismiss={() => feedback.dismiss(index)}
    />
  );
}
